/**
 * Match Cancelled Node - Player View
 *
 * Terminal state showing match cancellation with reason and acknowledge button.
 * Clean and compact — clear messaging without being dramatic.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { useState } from "react";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type MatchCancelledState = {
  reason: string;
  cancelledAt: number;
  acknowledgedBy: string[];
};

const REASON_LABELS: Record<string, string> = {
  timeout: "A player failed to ready up in time.",
  not_enough_players: "Not enough players to start the match.",
  host_cancelled: "The host cancelled the match.",
  error: "An unexpected error occurred.",
  unknown: "The match was cancelled.",
};

function formatReason(reason: string): string {
  return REASON_LABELS[reason] ?? reason;
}

export function MatchCancelledView({
  nodeState,
  players,
  onAction,
}: NodePlayerViewProps<MatchCancelledState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as MatchCancelledState;
  const config = nodeState.config as {
    reason?: string;
    displayMessage?: string;
    showReturnButton?: boolean;
    labels?: { title?: string };
  };
  const title = config.labels?.title ?? config.displayMessage ?? "Match Cancelled";
  const hasAcknowledged = state.acknowledgedBy?.includes(userId ?? "");
  const acknowledgeCount = state.acknowledgedBy?.length ?? 0;
  const totalPlayers = players.length;

  const displayReason = formatReason(state.reason ?? config.reason ?? "unknown");

  const handleAcknowledge = async () => {
    if (isSubmitting || hasAcknowledged) return;
    setIsSubmitting(true);
    setError(null);
    const result = await onAction("acknowledge", { userId });
    if (!result.success && result.error) {
      setError(result.error);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="@container relative overflow-hidden rounded-xl bg-surface-900 border border-surface-700/50">
      {/* Atmospheric background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_30%,rgba(239,68,68,0.03),transparent_70%)]" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-error-500/20 to-transparent" />
      </div>

      <div className="relative z-10 px-4 @md:px-8 py-5 @md:py-6">
        <div className="text-center">
          {/* Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-error-500/10 border border-error-500/20 mb-5"
          >
            <X className="size-7 text-error-400" strokeWidth={2.5} />
          </motion.div>

          {/* Title */}
          <motion.h3
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            className="font-display text-xl @sm:text-2xl font-bold uppercase tracking-[0.15em] text-error-400 mb-2"
          >
            {title}
          </motion.h3>

          {/* Reason */}
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
            className="text-surface-400 text-sm mb-6"
          >
            {displayReason}
          </motion.p>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="text-error-400 text-sm mb-4"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Acknowledge button / confirmed state */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.35 }}
          >
            <AnimatePresence mode="wait">
              {!hasAcknowledged ? (
                <motion.button
                  key="ack-button"
                  exit={{ opacity: 0, scale: 0.95 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAcknowledge}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-800/60 border border-surface-600/40 text-surface-200 font-semibold text-sm transition-all hover:bg-surface-800/80 hover:border-surface-500/40 focus:outline-none focus:ring-2 focus:ring-error-400/50 focus:ring-offset-2 focus:ring-offset-surface-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="w-4 h-4 rounded-full border-2 border-surface-500 border-t-surface-200 animate-spin" />
                  ) : (
                    <Check className="size-4 text-surface-400" />
                  )}
                  {isSubmitting ? "Acknowledging..." : "Acknowledge"}
                </motion.button>
              ) : (
                <motion.div
                  key="ack-done"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/[0.08] border border-brand-500/20"
                >
                  <Check className="size-4 text-brand-400" />
                  <span className="text-sm font-semibold text-brand-400 uppercase tracking-wider">
                    Acknowledged
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Count */}
            <p className="mt-3 text-xs text-surface-500">
              {acknowledgeCount} / {totalPlayers} acknowledged
            </p>
          </motion.div>

          {/* Return to queue */}
          <AnimatePresence>
            {hasAcknowledged && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="mt-5"
              >
                <span className="text-xs text-surface-500">Returning to queue...</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
